/**
 * Global theme configuration for consistent styling across the application.
 * Values are based on Tailwind CSS classes.
 * Supports light and dark modes via Tailwind's 'dark:' prefix.
 */

// Light theme constants
export const lightTheme = {
    colors: {
        gradientStart: 'rgb(225, 238, 249)',
        gradientEnd: 'rgb(238, 245, 251)',
        pageBg: 'bg-transparent',
        pageBgSolid: 'bg-[#EEF5FB]', // Matches gradientEnd
        cellBg: 'bg-white',
        cardBg: 'bg-white',
        border: 'border-gray-100',
        divider: 'divide-gray-100',

        // Accents
        primary: 'blue-600',
        primaryText: 'text-blue-600',
        primaryBg: 'bg-blue-600',
        primaryLight: 'bg-blue-50',

        secondary: 'gray-400',
        secondaryText: 'text-gray-400',

        error: 'red-600',
        errorText: 'text-red-600',
        errorBg: 'bg-red-600',
        errorLight: 'bg-red-50',

        success: 'green-500',
        successText: 'text-green-500',
        successBg: 'bg-green-500',
        successLight: 'bg-green-50',
    }
};

// Dark theme constants (Inverted colors logic)
export const darkTheme = {
    colors: {
        // gradientStart: 'rgba(88, 143, 194, 1)',
        gradientStart: 'rgb(0, 48, 94)',
        gradientEnd: 'rgb(0, 13, 25)',
        pageBg: 'bg-transparent',
        pageBgSolid: 'bg-[#000D19]', // Matches gradientEnd
        cellBg: 'bg-slate-900/60',
        cardBg: 'bg-slate-900/60',
        border: 'border-white/10',
        divider: 'divide-white/10',

        // Accents (Adjusted for dark mode visibility)
        primary: 'blue-500',
        primaryText: 'text-blue-400',
        primaryBg: 'bg-blue-600',
        primaryLight: 'bg-blue-900/30',

        secondary: 'gray-500',
        secondaryText: 'text-gray-500',

        error: 'red-500',
        errorText: 'text-red-400',
        errorBg: 'bg-red-600',
        errorLight: 'bg-red-900/30',

        success: 'green-500',
        successText: 'text-green-400',
        successBg: 'bg-green-600',
        successLight: 'bg-green-900/30',
    }
};

// Combined theme that handles both modes automatically using Tailwind 'dark:' variants
export const theme = {
    colors: {
        pageBg: 'bg-transparent',
        pageBgSolid: 'bg-[#EEF5FB] dark:bg-[#000D19]',
        cellBg: 'bg-white dark:bg-slate-900/60',
        cardBg: 'bg-white dark:bg-slate-900/60',
        border: 'border-gray-100 dark:border-white/10',
        divider: 'divide-gray-100 dark:divide-white/10',

        // Accents
        primary: 'blue-600 dark:text-blue-400',
        primaryText: 'text-blue-600 dark:text-blue-400',
        primaryBg: 'bg-blue-600 dark:bg-blue-500',
        primaryLight: 'bg-blue-50 dark:bg-blue-900/40',

        secondary: 'gray-400 dark:gray-500',
        secondaryText: 'text-gray-400 dark:text-gray-500',

        error: 'red-600 dark:red-500',
        errorText: 'text-red-600 dark:text-red-400',
        errorBg: 'bg-red-600 dark:bg-red-600',
        errorLight: 'bg-red-50 dark:bg-red-900/30',

        success: 'green-500 dark:green-500',
        successText: 'text-green-500 dark:text-green-400',
        successBg: 'bg-green-500 dark:bg-green-600',
        successLight: 'bg-green-50 dark:bg-green-900/30',
    },

    typography: {
        // Text Primary: auto-adjust relative to background
        sectionTitle: 'text-xl font-black text-gray-900 dark:text-white font-inter',
        sectionLabel: 'block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-inter',
        prefLabel: 'text-[15px] font-semibold text-gray-900 dark:text-gray-100 leading-tight font-inter',
        itemTitle: 'text-[15px] font-semibold text-gray-900 dark:text-gray-100 leading-tight font-inter',
        itemSubtitle: 'text-[13px] font-medium text-gray-400 dark:text-gray-500 font-inter',
        smallLabel: 'text-[13px] font-semibold text-gray-500 dark:text-gray-400 font-inter',
    },

    spacing: {
        pagePadding: 'px-4 max-w-2xl mx-auto space-y-8',
        sectionGap: 'space-y-4',
        cardPadding: 'p-4',
        itemGap: 'gap-4',
        iconContainer: 'w-10 h-10',
    },

    radius: {
        card: 'rounded-[2rem]',
        button: 'rounded-full',
        input: 'rounded-xl',
    },

    effects: {
        shadow: 'shadow-sm dark:shadow-none',
        shadowHover: 'hover:shadow-md dark:hover:shadow-none',
        activeScale: 'active:scale-[0.99]',
        transition: 'transition-all duration-200',
    }
};

export const styles = {
    pageContainer: `min-h-screen ${theme.colors.pageBg} font-inter`,
    card: `${theme.colors.cardBg} ${theme.radius.card} border ${theme.colors.border} overflow-hidden`,
    listItem: `w-full flex items-center justify-between ${theme.spacing.cardPadding} hover:bg-gray-50/80 dark:hover:bg-white/5 ${theme.effects.transition} ${theme.effects.activeScale}`,
    outlinedActive: `border-2 border-blue-600 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-400/10 text-blue-600 dark:text-blue-400 shadow-sm`,
    outlinedInactive: `border-2 border-transparent bg-gray-50 dark:bg-slate-900/40 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5`,
    sectionHeader: `flex items-center justify-between px-2`,
};
